// VHS / CRT effect layer for horrible-fm
// Layers signal-level artifacts (noise, chroma bleed, ghosting, jitter, tracking
// errors, head-switching) and glass-level artifacts (scanlines, phosphor mask,
// vignette, flicker, glare) over the WeatherStar display, with a control panel
// to dial everything in. Inspired by the horrible-site WebGL CRT shader, adapted
// to live DOM via CSS/SVG filters and canvas overlays.

const CONTAINER_SELECTOR = '#container';

// Settings are intentionally NOT persisted: every reload starts fresh from the
// baked-in defaults below (the knob schema). Dialing the panel changes the live
// effect for the session only. To change the startup look, edit the defaults in
// the knob schema (or a PRESET), not localStorage.

// ---------------------------------------------------------------------------
// Knob schema: [key, label, min, max, step, default, unit]
// ---------------------------------------------------------------------------
const GROUPS = [
	{
		name: 'Tuning',
		knobs: [
			// Master "detune the VHF dial" macro. 0 = locked on station; toward
			// either extreme the station drifts off — color dies, chroma fringes,
			// the picture rolls, herringbone beats appear, snow rises, and at the
			// far edge the signal flips negative. Sign picks which way the hue pulls
			// and which way the picture rolls. Drives tuneMacro(); see below.
			['tuning', 'VHF tuning', -1, 1, 0.01, 0, ''],
			['tuneRoll', 'Roll amount', 0, 1, 0.01, 0.7, ''],
		],
	},
	{
		name: 'Picture',
		knobs: [
			['brightness', 'Brightness', 0.5, 1.6, 0.01, 1.05, 'x'],
			['contrast', 'Contrast', 0.5, 1.6, 0.01, 1.12, 'x'],
			['saturation', 'Saturation', 0, 2, 0.01, 1.12, 'x'],
			['hue', 'Hue', -45, 45, 1, 0, 'deg'],
			['softness', 'Softness', 0, 2, 0.05, 0.6, 'px'],
			['bloom', 'Phosphor bloom', 0, 1, 0.01, 0.48, ''],
			['bloomSize', 'Bloom size', 0, 8, 0.1, 2.1, 'px'],
		],
	},
	{
		name: 'Signal',
		knobs: [
			['chroma', 'Chroma shift', 0, 8, 0.1, 2.2, 'px'],
			['ghost', 'Ghost offset', 0, 24, 1, 7, 'px'],
			['ghostOpacity', 'Ghost strength', 0, 0.6, 0.01, 0.06, ''],
			['noise', 'Static noise', 0, 1, 0.01, 0.07, ''],
			['grain', 'Grain size', 1, 6, 1, 1, 'px'],
			['noiseFps', 'Noise FPS', 6, 60, 1, 13, 'fps'],
		],
	},
	{
		name: 'Tape transport',
		knobs: [
			['jitter', 'Line jitter', 0, 4, 0.05, 0.1, 'px'],
			['flutter', 'Wow & flutter', 0, 6, 0.05, 0.12, 'px'],
			['flutterSpeed', 'Flutter speed', 0.05, 2, 0.05, 0.1, 'Hz'],
			['snapAmount', 'Snap jitter', 0, 20, 0.5, 2.5, 'px'],
			['snapFreq', 'Snap every', 0, 30, 0.5, 30, 's'],
			['snapDuration', 'Snap duration', 40, 400, 10, 110, 'ms'],
			['trackingFreq', 'Tracking err every', 0, 60, 1, 24, 's'],
			['trackingIntensity', 'Tracking severity', 0, 1, 0.01, 0.79, ''],
			['headSwitch', 'Head-switch noise', 0, 1, 0.01, 0.11, ''],
			['headSwitchHeight', 'Head-switch height', 0, 24, 1, 12, 'px'],
		],
	},
	{
		name: 'Power on',
		knobs: [
			['fadeDuration', 'Fade-in', 0, 8, 0.1, 5, 's'],
		],
	},
	{
		name: 'CRT glass',
		knobs: [
			['scanlines', 'Scanlines', 0, 1, 0.01, 0.4, ''],
			['scanSpacing', 'Scanline spacing', 2, 8, 1, 2, 'px'],
			['phosphor', 'Phosphor mask', 0, 1, 0.01, 0.26, ''],
			['flicker', 'Flicker', 0, 1, 0.01, 0.44, ''],
			['vignette', 'Vignette', 0, 1, 0.01, 0.82, ''],
			['humBar', 'Hum bar', 0, 1, 0.01, 0.28, ''],
			['blackLift', 'CRT black lift', 0, 30, 1, 1, ''],
			['cornerRadius', 'Corner radius', 0, 40, 1, 27, 'px'],
			['glare', 'Glass glare', 0, 1, 0.01, 0.36, ''],
		],
	},
];

const DEFAULTS = Object.fromEntries(GROUPS.flatMap((g) => g.knobs.map((k) => [k[0], k[5]])));

const PRESETS = {
	Broadcast: {
		chroma: 0.6,
		ghost: 2,
		ghostOpacity: 0.06,
		noise: 0.03,
		snapAmount: 0,
		snapFreq: 0,
		trackingFreq: 0,
		headSwitch: 0.08,
		headSwitchHeight: 4,
		scanlines: 0.3,
		flicker: 0.1,
		softness: 0.2,
		bloom: 0.12,
	},
	"Cable '95": { ...{} }, // defaults
	'VHS SP': {
		chroma: 2.2,
		ghost: 8,
		noise: 0.18,
		jitter: 1,
		snapAmount: 7,
		snapFreq: 6,
		trackingFreq: 25,
		trackingIntensity: 0.6,
		headSwitch: 0.6,
		flicker: 0.22,
	},
	'VHS EP': {
		chroma: 3.5,
		ghost: 12,
		ghostOpacity: 0.25,
		noise: 0.3,
		jitter: 1.8,
		flutter: 3.5,
		snapAmount: 10,
		snapFreq: 4,
		snapDuration: 150,
		trackingFreq: 10,
		trackingIntensity: 0.85,
		headSwitch: 0.85,
		headSwitchHeight: 16,
		softness: 0.8,
		flicker: 0.3,
		saturation: 1.05,
	},
	Detuned: {
		tuning: 0.5,
		tuneRoll: 0.7,
		chroma: 1.2,
		ghost: 6,
		ghostOpacity: 0.12,
		noise: 0.08,
		snapAmount: 4,
		snapFreq: 8,
		headSwitch: 0.2,
		softness: 0.4,
		flicker: 0.15,
	},
	Dumpster: {
		chroma: 5,
		ghost: 18,
		ghostOpacity: 0.35,
		noise: 0.5,
		jitter: 3,
		flutter: 5,
		flutterSpeed: 0.6,
		snapAmount: 16,
		snapFreq: 2,
		snapDuration: 200,
		trackingFreq: 5,
		trackingIntensity: 1,
		headSwitch: 1,
		headSwitchHeight: 20,
		softness: 1.2,
		flicker: 0.4,
		saturation: 0.9,
		brightness: 0.98,
		humBar: 0.15,
	},
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
const state = {
	enabled: true,
	settings: { ...DEFAULTS },
	rafId: null,
	lastNoiseTime: 0,
	glitch: null, // { until, intensity }
	nextGlitchAt: 0,
	snap: null, // { start, duration, amp }
	nextSnapAt: 0,
	container: null,
	els: {}, // overlay elements
	noiseCtx: null,
	offCanvas: null,
	offCtx: null,
	offImageData: null,
	offSize: { w: 0, h: 0 },
};

// ---------------------------------------------------------------------------
// SVG filter (chroma split + ghost + bloom)
// ---------------------------------------------------------------------------
const injectSvgFilter = () => {
	const svg = `
	<svg id="vhs-svg" width="0" height="0" aria-hidden="true" focusable="false">
		<filter id="vhs-signal" x="-10%" y="-10%" width="120%" height="120%" color-interpolation-filters="sRGB">
			<feColorMatrix in="SourceGraphic" type="matrix" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="r"/>
			<feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" result="g"/>
			<feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" result="b"/>
			<feOffset id="vhs-fe-ro" in="r" dx="1.4" dy="0" result="ro"/>
			<feOffset id="vhs-fe-bo" in="b" dx="-1.4" dy="0" result="bo"/>
			<feComposite in="ro" in2="g" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="rg"/>
			<feComposite in="rg" in2="bo" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="rgb"/>
			<feOffset id="vhs-fe-ghost-o" in="rgb" dx="5" dy="0" result="ghostsrc"/>
			<feComponentTransfer in="ghostsrc" result="ghost">
				<feFuncR id="vhs-fe-ghost-r" type="linear" slope="0.14" intercept="0"/>
				<feFuncG id="vhs-fe-ghost-g" type="linear" slope="0.14" intercept="0"/>
				<feFuncB id="vhs-fe-ghost-b" type="linear" slope="0.14" intercept="0"/>
			</feComponentTransfer>
			<feBlend in="rgb" in2="ghost" mode="screen" result="withghost"/>
			<feGaussianBlur id="vhs-fe-bloom-blur" in="withghost" stdDeviation="2.5" result="bloomblur"/>
			<feComponentTransfer in="bloomblur" result="bloom">
				<feFuncR id="vhs-fe-bloom-r" type="linear" slope="0.18" intercept="0"/>
				<feFuncG id="vhs-fe-bloom-g" type="linear" slope="0.18" intercept="0"/>
				<feFuncB id="vhs-fe-bloom-b" type="linear" slope="0.18" intercept="0"/>
			</feComponentTransfer>
			<feBlend in="withghost" in2="bloom" mode="screen"/>
		</filter>
	</svg>`;
	// keep the filter defs inside #divTwc: kiosk mode display:none's everything
	// else on the body, which would break the url(#vhs-signal) reference
	document.querySelector('#divTwc').insertAdjacentHTML('beforeend', svg);
};

const setAttr = (id, attr, value) => {
	const el = document.getElementById(id);
	if (el) el.setAttribute(attr, value);
};

const updateSvgFilter = (chromaOverride = null) => {
	const s = state.settings;
	const chroma = (chromaOverride ?? s.chroma) + tuneMacro().chromaBoost;
	setAttr('vhs-fe-ro', 'dx', chroma);
	setAttr('vhs-fe-bo', 'dx', -chroma);
	setAttr('vhs-fe-ghost-o', 'dx', s.ghost);
	['r', 'g', 'b'].forEach((c) => setAttr(`vhs-fe-ghost-${c}`, 'slope', s.ghostOpacity));
	setAttr('vhs-fe-bloom-blur', 'stdDeviation', s.bloomSize);
	['r', 'g', 'b'].forEach((c) => setAttr(`vhs-fe-bloom-${c}`, 'slope', s.bloom));
};

// ---------------------------------------------------------------------------
// Overlay elements
// ---------------------------------------------------------------------------
const buildOverlays = () => {
	const make = (id, tag = 'div') => {
		const el = document.createElement(tag);
		el.id = id;
		el.classList.add('vhs-overlay');
		state.container.append(el);
		return el;
	};

	state.els.blackLift = make('vhs-blacklift');
	const noise = make('vhs-noise', 'canvas');
	noise.width = 640;
	noise.height = 480;
	state.noiseCtx = noise.getContext('2d');
	state.els.noise = noise;
	state.els.tracking = make('vhs-tracking');
	state.els.humBar = make('vhs-hum');
	// tuning artifacts — signal-level, so they sit under the glass overlays
	state.els.herring = make('vhs-herring');
	state.els.roll = make('vhs-roll');
	state.els.scanlines = make('vhs-scanlines');
	state.els.phosphor = make('vhs-phosphor');
	state.els.vignette = make('vhs-vignette');
	state.els.glare = make('vhs-glare');
	state.els.flicker = make('vhs-flicker');

	// offscreen noise source
	state.offCanvas = document.createElement('canvas');
	state.offCtx = state.offCanvas.getContext('2d');
};

// ---------------------------------------------------------------------------
// VHF tuning macro
// ---------------------------------------------------------------------------
// Turns the single `tuning` dial (-1..1, 0 = locked) into the bundle of derived
// effects that together read as detuning a real VHF tuner. |tuning| = how far
// off station; the sign picks hue direction and roll direction. Pure function of
// state.settings — read by buildFilterString (static filter parts), updateSvgFilter
// (chroma fringe), drawNoise (snow), and tick (roll). This is an *imitation* of
// RF detune, not a real demodulator: rainbow is hue-rotate, not subcarrier phase.
const round3 = (n) => Math.round(n * 1000) / 1000;

const tuneMacro = () => {
	const t = state.settings.tuning || 0;
	const d = Math.min(1, Math.abs(t)); // distance off station, 0..1
	const rollAmt = state.settings.tuneRoll ?? 0;
	return {
		t,
		d,
		active: d > 0.001,
		// color-killer drops out: saturation collapses to B&W by ~70% off
		satFactor: Math.max(0, 1 - d / 0.7),
		// signal weakens: dims and loses contrast
		brightFactor: 1 - 0.28 * d,
		contrastFactor: 1 - 0.12 * d,
		// subcarrier reference drifts: whole image pulls toward green/magenta
		hueShift: t * 45,
		// chroma demod smears sideways: extra R/B split → rainbow fringe
		chromaBoost: d * 6,
		// snow rises out of the weakening carrier
		noiseBoost: d * 0.55,
		// AM detector inverts once the carrier is basically gone
		invert: d > 0.85,
		// vertical-hold loss past ~30% off; direction follows the dial sign
		rollActive: rollAmt > 0 && d > 0.3,
		rollSpeed: (d > 0.3 ? (d - 0.3) / 0.7 : 0) * (t < 0 ? -1 : 1),
		rollOpacity: Math.min(1, Math.max(0, (d - 0.3) / 0.7)) * rollAmt,
		rollBob: Math.max(0, (d - 0.3) / 0.7) * 12 * rollAmt,
		// intercarrier beat → herringbone interference
		herring: d,
	};
};

// ---------------------------------------------------------------------------
// Static (knob-change-time) style application
// ---------------------------------------------------------------------------
// Builds the container filter chain
const buildFilterString = () => {
	const s = state.settings;
	const m = tuneMacro();
	// skip svg url() when its knobs are all zero
	const svgActive = s.chroma > 0 || m.chromaBoost > 0 || (s.ghost > 0 && s.ghostOpacity > 0) || s.bloom > 0;
	const parts = [];
	if (svgActive) parts.push('url(#vhs-signal)');
	const brightness = s.brightness * m.brightFactor;
	const contrast = s.contrast * m.contrastFactor;
	const saturation = s.saturation * m.satFactor;
	const hue = s.hue + m.hueShift;
	if (Math.abs(brightness - 1) > 0.001) parts.push(`brightness(${round3(brightness)})`);
	if (Math.abs(contrast - 1) > 0.001) parts.push(`contrast(${round3(contrast)})`);
	if (Math.abs(saturation - 1) > 0.001) parts.push(`saturate(${round3(saturation)})`);
	if (Math.round(hue) !== 0) parts.push(`hue-rotate(${round3(hue)}deg)`);
	if (s.softness > 0) parts.push(`blur(${s.softness}px)`);
	if (m.invert) parts.push('invert(1)');
	return parts.join(' ');
};

// duration of the power-on fade lives in a CSS var so the fade works even when
// effects are disabled; set independent of the enabled gate below
const applyFadeDuration = () => {
	document.documentElement.style.setProperty('--vhs-fade-duration', `${state.settings.fadeDuration * 1000}ms`);
};

const applyStatic = () => {
	const s = state.settings;
	const c = state.container;
	const { els } = state;
	if (!c) return;

	applyFadeDuration();

	if (!state.enabled) {
		c.style.filter = '';
		c.style.translate = '';
		c.style.borderRadius = '';
		c.style.overflow = '';
		Object.values(els).forEach((el) => { el.style.display = 'none'; });
		return;
	}
	Object.values(els).forEach((el) => { el.style.display = ''; });

	c.style.filter = buildFilterString();
	updateSvgFilter();

	// glass
	els.scanlines.style.background = s.scanlines > 0
		? `repeating-linear-gradient(to bottom, rgba(0,0,0,${s.scanlines}) 0px, rgba(0,0,0,${s.scanlines}) 1px, transparent 1px, transparent ${s.scanSpacing}px)`
		: 'none';
	els.phosphor.style.opacity = s.phosphor;
	els.vignette.style.opacity = s.vignette;

	// tuning overlays: intensity tracks the dial; roll position is animated in tick
	const m = tuneMacro();
	els.herring.style.opacity = (m.herring * 0.5).toFixed(3);
	els.roll.style.opacity = m.rollOpacity.toFixed(3);
	els.roll.style.display = m.rollActive ? '' : 'none';
	els.glare.style.opacity = s.glare;
	els.humBar.style.opacity = s.humBar;
	const bl = s.blackLift;
	els.blackLift.style.background = `rgb(${bl}, ${bl + 2}, ${bl})`;
	els.blackLift.style.display = bl > 0 ? '' : 'none';

	if (s.cornerRadius > 0) {
		c.style.borderRadius = `${s.cornerRadius}px`;
		c.style.overflow = 'hidden';
	} else {
		c.style.borderRadius = '';
		c.style.overflow = '';
	}
};

// ---------------------------------------------------------------------------
// Noise drawing
// ---------------------------------------------------------------------------
const drawNoise = (now) => {
	const s = state.settings;
	const tuneNoise = tuneMacro().noiseBoost;
	if (s.noise <= 0 && s.headSwitch <= 0 && tuneNoise <= 0) {
		if (state.els.noise.style.visibility !== 'hidden') {
			state.noiseCtx.clearRect(0, 0, 640, 480);
			state.els.noise.style.visibility = 'hidden';
		}
		return;
	}
	state.els.noise.style.visibility = '';
	if (now - state.lastNoiseTime < 1000 / s.noiseFps) return;
	state.lastNoiseTime = now;

	const grain = state.glitch ? Math.max(s.grain, 2) : s.grain;
	const w = Math.ceil(640 / grain);
	const h = Math.ceil(480 / grain);
	if (state.offSize.w !== w || state.offSize.h !== h) {
		state.offCanvas.width = w;
		state.offCanvas.height = h;
		state.offImageData = state.offCtx.createImageData(w, h);
		state.offSize = { w, h };
	}
	const d = state.offImageData.data;
	for (let i = 0; i < d.length; i += 4) {
		const v = Math.floor(Math.random() * 255);
		d[i] = v;
		d[i + 1] = v;
		d[i + 2] = v;
		d[i + 3] = 255;
	}
	state.offCtx.putImageData(state.offImageData, 0, 0);

	const ctx = state.noiseCtx;
	ctx.clearRect(0, 0, 640, 480);
	ctx.imageSmoothingEnabled = false;

	// base static
	const glitchBoost = state.glitch ? 0.25 * state.glitch.intensity : 0;
	const amount = Math.min(1, s.noise + glitchBoost + tuneNoise);
	if (amount > 0) {
		ctx.globalAlpha = amount;
		ctx.drawImage(state.offCanvas, 0, 0, w, h, 0, 0, 640, 480);
	}

	// head-switching noise at bottom of frame
	if (s.headSwitch > 0 && s.headSwitchHeight > 0) {
		const hh = s.headSwitchHeight;
		ctx.globalAlpha = Math.min(1, s.headSwitch * 0.9);
		for (let y = 480 - hh; y < 480; y += 2) {
			const dx = (Math.random() * 36 - 12) * s.headSwitch;
			const sy = Math.floor(Math.random() * h);
			ctx.drawImage(state.offCanvas, 0, sy, w, 1, dx, y, 640, 2);
		}
		// occasional bright tear line
		if (Math.random() < 0.4) {
			ctx.globalAlpha = 1;
			ctx.fillStyle = `rgba(255,255,255,${0.35 * s.headSwitch})`;
			ctx.fillRect(Math.random() * 400, 480 - hh + Math.random() * hh, 100 + Math.random() * 200, 1.5);
		}
	}
	ctx.globalAlpha = 1;
};

// ---------------------------------------------------------------------------
// Tracking glitch events
// ---------------------------------------------------------------------------
const scheduleNextGlitch = (now) => {
	const freq = state.settings.trackingFreq;
	if (freq <= 0) {
		state.nextGlitchAt = Infinity;
		return;
	}
	// randomize +/- 50% around the average interval
	state.nextGlitchAt = now + freq * 1000 * (0.5 + Math.random());
};

// ---------------------------------------------------------------------------
// Snap jitter: infrequent sharp horizontal displacement that springs back fast
// ---------------------------------------------------------------------------
const scheduleNextSnap = (now) => {
	const freq = state.settings.snapFreq;
	if (freq <= 0 || state.settings.snapAmount <= 0) {
		state.nextSnapAt = Infinity;
		return;
	}
	// randomize +/- 50% around the average interval
	state.nextSnapAt = now + freq * 1000 * (0.5 + Math.random());
};

const snapOffset = (now) => {
	const s = state.settings;
	if (!state.snap && now >= state.nextSnapAt) {
		state.snap = {
			start: now,
			duration: s.snapDuration * (0.7 + 0.6 * Math.random()),
			amp: s.snapAmount * (0.6 + 0.4 * Math.random()) * (Math.random() < 0.5 ? -1 : 1),
		};
	}
	if (!state.snap) return 0;

	const p = (now - state.snap.start) / state.snap.duration;
	if (p >= 1) {
		state.snap = null;
		scheduleNextSnap(now);
		return 0;
	}
	// hold displaced with a slight buzz, then spring back with one overshoot swing
	if (p < 0.6) return state.snap.amp * (1 + (Math.random() - 0.5) * 0.15);
	const q = (p - 0.6) / 0.4;
	return state.snap.amp * (1 - q) * Math.cos(q * Math.PI * 1.5);
};

const startGlitch = (now, intensityOverride = null) => {
	const intensity = intensityOverride ?? state.settings.trackingIntensity;
	const duration = 150 + Math.random() * 350 * intensity;
	state.glitch = { start: now, until: now + duration, intensity };
	state.els.tracking.style.display = 'block';
	updateSvgFilter(state.settings.chroma + 4 * intensity);
};

const endGlitch = (now) => {
	state.glitch = null;
	state.els.tracking.style.display = 'none';
	updateSvgFilter();
	scheduleNextGlitch(now);
};

// ---------------------------------------------------------------------------
// Main loop
// ---------------------------------------------------------------------------
const tick = (now) => {
	state.rafId = requestAnimationFrame(tick);
	const s = state.settings;
	const t = now / 1000;
	const scale = window.currentScale || 1;

	// glitch lifecycle
	if (!state.glitch && now >= state.nextGlitchAt) startGlitch(now);
	if (state.glitch && now >= state.glitch.until) endGlitch(now);

	// jitter + wow/flutter
	let x = 0;
	let y = 0;
	if (s.jitter > 0) {
		x += (Math.random() - 0.5) * 2 * s.jitter;
		y += (Math.random() - 0.5) * 0.6 * s.jitter;
	}
	if (s.flutter > 0) {
		x += Math.sin(t * Math.PI * 2 * s.flutterSpeed) * s.flutter
			+ Math.sin(t * Math.PI * 2 * s.flutterSpeed * 3.7) * s.flutter * 0.3;
	}
	x += snapOffset(now);
	if (state.glitch) {
		const g = state.glitch.intensity;
		x += (Math.random() - 0.5) * 14 * g;
		y += (Math.random() - 0.5) * 5 * g;
	}

	// vertical-hold loss: a dark blanking bar sweeps through and the picture
	// creeps then jumps (sawtooth), reading as the frame rolling
	const m = tuneMacro();
	if (m.rollActive) {
		const phase = ((t * m.rollSpeed * 0.5) % 1 + 1) % 1;
		state.els.roll.style.top = `${phase * 114 - 7}%`;
		y += (0.5 - phase) * m.rollBob;
	}

	state.container.style.translate = (x !== 0 || y !== 0) ? `${x * scale}px ${y * scale}px` : '';

	// tracking band position
	if (state.glitch) {
		const progress = (now - state.glitch.start) / (state.glitch.until - state.glitch.start);
		state.els.tracking.style.top = `${progress * 110 - 5}%`;
	}

	// flicker: 60Hz-ish + slow beat, rendered as a black overlay opacity
	if (s.flicker > 0) {
		const f = (Math.sin(t * 60 * Math.PI * 2) * 0.5 + Math.sin(t * 13.7) * 0.4 + 0.9) / 1.8;
		state.els.flicker.style.opacity = (f * 0.09 * s.flicker).toFixed(4);
	} else {
		state.els.flicker.style.opacity = 0;
	}

	drawNoise(now);
};

const start = () => {
	if (state.rafId === null) {
		scheduleNextGlitch(performance.now());
		scheduleNextSnap(performance.now());
		state.rafId = requestAnimationFrame(tick);
	}
};

const stop = () => {
	if (state.rafId !== null) {
		cancelAnimationFrame(state.rafId);
		state.rafId = null;
	}
	if (state.glitch) endGlitch(performance.now());
};

// Fade the whole tube up from black. The CSS transition on #divTwc does the
// work; removing .vhs-boot-pending triggers it. Wait for fonts + layout +
// kiosk scaling to settle first so the Please Stand By card is painted at its
// final size — no pop — before the fade starts.
const revealWhenReady = async () => {
	applyFadeDuration();

	const windowLoaded = document.readyState === 'complete'
		? Promise.resolve()
		: new Promise((resolve) => { window.addEventListener('load', resolve, { once: true }); });
	const doubleRaf = new Promise((resolve) => {
		requestAnimationFrame(() => requestAnimationFrame(resolve));
	});
	// never leave the TV dark: cap the wait even if an asset stalls
	const failsafe = new Promise((resolve) => { setTimeout(resolve, 3000); });

	await Promise.race([Promise.all([document.fonts.ready, windowLoaded, doubleRaf]), failsafe]);

	// the element has been painted at opacity 0 for many frames, so removing
	// the class now animates via the transition
	document.documentElement.classList.remove('vhs-boot-pending');
};

// Replay the fade-in: snap to black (transition disabled by the class), force
// that 0-opacity to commit, then remove the class to fade back up.
const replayFadeIn = () => {
	applyFadeDuration();
	document.documentElement.classList.add('vhs-boot-pending');
	state.container.getBoundingClientRect(); // force reflow so 0-opacity commits first
	document.documentElement.classList.remove('vhs-boot-pending');
};

// ---------------------------------------------------------------------------
// Control panel
// ---------------------------------------------------------------------------
const buildPanel = () => {
	const panel = document.createElement('div');
	panel.id = 'vhs-panel';
	panel.classList.add('collapsed');

	const toggle = document.createElement('button');
	toggle.id = 'vhs-panel-toggle';
	toggle.type = 'button';
	toggle.textContent = '📼 VHS';
	toggle.addEventListener('click', () => panel.classList.toggle('collapsed'));
	panel.append(toggle);

	const body = document.createElement('div');
	body.className = 'vhs-panel-body';
	panel.append(body);

	// header: master toggle + presets + copy
	const header = document.createElement('div');
	header.className = 'vhs-panel-header';

	const masterLabel = document.createElement('label');
	masterLabel.className = 'vhs-master';
	const master = document.createElement('input');
	master.type = 'checkbox';
	master.checked = state.enabled;
	master.addEventListener('change', () => {
		state.enabled = master.checked;
		applyStatic();
		if (state.enabled) start(); else { stop(); applyStatic(); }
	});
	masterLabel.append(master, document.createTextNode(' POWER'));
	header.append(masterLabel);

	const presetRow = document.createElement('div');
	presetRow.className = 'vhs-presets';
	Object.keys(PRESETS).forEach((name) => {
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.textContent = name;
		btn.addEventListener('click', () => {
			state.settings = { ...DEFAULTS, ...PRESETS[name] };
			refreshSliders();
			applyStatic();
			scheduleNextGlitch(performance.now());
			scheduleNextSnap(performance.now());
		});
		presetRow.append(btn);
	});
	header.append(presetRow);

	const copyBtn = document.createElement('button');
	copyBtn.type = 'button';
	copyBtn.className = 'vhs-copy';
	copyBtn.textContent = 'Copy settings JSON';
	copyBtn.addEventListener('click', async () => {
		try {
			await navigator.clipboard.writeText(JSON.stringify(state.settings, null, 2));
			copyBtn.textContent = 'Copied ✓';
		} catch {
			copyBtn.textContent = 'Copy failed';
		}
		setTimeout(() => { copyBtn.textContent = 'Copy settings JSON'; }, 1500);
	});
	header.append(copyBtn);

	const replayBtn = document.createElement('button');
	replayBtn.type = 'button';
	replayBtn.className = 'vhs-copy';
	replayBtn.textContent = '▶ Replay fade-in';
	replayBtn.addEventListener('click', replayFadeIn);
	header.append(replayBtn);
	body.append(header);

	// knob groups
	const sliderRefs = {};
	GROUPS.forEach((group) => {
		const details = document.createElement('details');
		details.open = true;
		const summary = document.createElement('summary');
		summary.textContent = group.name;
		details.append(summary);

		group.knobs.forEach(([key, label, min, max, step, def, unit]) => {
			const row = document.createElement('div');
			row.className = 'vhs-knob';

			const lab = document.createElement('label');
			lab.textContent = label;
			lab.title = `Double-click to reset (${def}${unit})`;

			const input = document.createElement('input');
			input.type = 'range';
			input.min = min;
			input.max = max;
			input.step = step;
			input.value = state.settings[key];

			const readout = document.createElement('span');
			readout.className = 'vhs-value';

			const fmt = (v) => `${Number(v)}${unit}`;
			readout.textContent = fmt(state.settings[key]);

			input.addEventListener('input', () => {
				state.settings[key] = Number(input.value);
				readout.textContent = fmt(input.value);
				applyStatic();
				if (key === 'trackingFreq') scheduleNextGlitch(performance.now());
				if (key === 'snapFreq' || key === 'snapAmount') scheduleNextSnap(performance.now());
			});
			lab.addEventListener('dblclick', () => {
				state.settings[key] = def;
				input.value = def;
				readout.textContent = fmt(def);
				applyStatic();
			});

			sliderRefs[key] = { input, readout, fmt };
			row.append(lab, input, readout);
			details.append(row);
		});
		body.append(details);
	});

	const refreshSliders = () => {
		Object.entries(sliderRefs).forEach(([key, ref]) => {
			ref.input.value = state.settings[key];
			ref.readout.textContent = ref.fmt(state.settings[key]);
		});
	};

	// attach inside #divTwc so the panel survives fullscreen mode
	document.querySelector('#divTwc').append(panel);
};

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
	state.container = document.querySelector(CONTAINER_SELECTOR);
	if (!state.container) return;
	// no loadSettings(): always start from the baked-in DEFAULTS (state init).
	// purge any blob left by older builds that used to persist settings
	try { localStorage.removeItem('vhs-settings-v1'); } catch { /* ignore */ }
	injectSvgFilter();
	buildOverlays();
	buildPanel();
	applyStatic();
	if (state.enabled) start();
	revealWhenReady();
});

export default null;
